import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Popover,
  Pagination,
  Snackbar,
  Alert,
  Badge,
  Tabs,
  Tab,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows, getBookingStatusStyle } from "./bookings.styles";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BookingFilters from "./BookingFilters";
import BookingRowMenu from "./BookingRowMenu";
import EditBookingModal from "./EditBookingModal";
import CancelAppointmentModal from "./CancelAppointmentModal";
import { updateBookingApi, deleteBookingApi } from "../../../api/bookings.api";

// Helper function to get service badge colors and abbreviation
const getServiceStyle = (serviceName) => {
  const name = serviceName?.toLowerCase() || "";
  const letters = serviceName?.slice(0, 2).toUpperCase() || "XX";

  if (name.includes("skin") || name.includes("face")) {
    return {
      letters,
      bg: "#FEF3C7",
      color: "#D97706",
    };
  }
  if (name.includes("dental") || name.includes("teeth")) {
    return {
      letters,
      bg: "#DBEAFE",
      color: "#2563EB",
    };
  }
  if (name.includes("hair")) {
    return {
      letters,
      bg: "#E0E7FF",
      color: "#6366F1",
    };
  }
  if (name.includes("beauty") || name.includes("makeup")) {
    return {
      letters,
      bg: "#FCE7F3",
      color: "#DB2777",
    };
  }

  // Default
  return {
    letters,
    bg: "#F3E8FF",
    color: "#9333EA",
  };
};

const BOOKING_TYPES = {
  test2: "Consultancy",
  treatment: "Treatment",
  all: "All Bookings",
};

const BOOKING_TYPE_COLORS = {
  test2: { color: "#3B82F6", bg: "#EFF6FF", label: "CONSULTANCY" },
  treatment: { color: "#8B5CF6", bg: "#F5F3FF", label: "TREATMENT" },
  consultancy: { color: "#3B82F6", bg: "#EFF6FF", label: "CONSULTANCY" },
  appointment: { color: "#3B82F6", bg: "#EFF6FF", label: "CONSULTANCY" },
  default: { color: "#64748B", bg: "#F8FAFC", label: "APPOINTMENT" },
};

const getBookingTypeStyle = (type) => {
  const typeLower = type?.toLowerCase();
  // Map legacy types to test2/consultancy
  if (typeLower === "test2" || typeLower === "consultancy" || typeLower === "appointment") {
    return BOOKING_TYPE_COLORS.test2;
  }
  return BOOKING_TYPE_COLORS[typeLower] || BOOKING_TYPE_COLORS.default;
};

const BookingHistory = ({ data = [], onFilter, totalCount = 1248 }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [bookingTypeTab, setBookingTypeTab] = useState("all");
  const rowsPerPage = 10;

  // Flatten the bookings data from leads structure
  const flattenedBookings = useMemo(() => {
    const result = [];
    data.forEach((lead) => {
      if (lead.bookings && Array.isArray(lead.bookings)) {
        lead.bookings.forEach((booking) => {
          result.push({
            ...booking,
            leadId: lead._id,
            clientName: lead.client_name,
            contact: lead.contact,
            email: lead.email,
            services: lead.services,
            leadData: lead,
          });
        });
      }
    });
    return result;
  }, [data]);

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 1.5,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 1.5,
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setPage(1);
    onFilter?.(filters);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setActiveFilters(null);
    setPage(1);
    onFilter?.(null);
  };

  const handleMenuClick = (event, booking) => {
    setMenuAnchor(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewDetails = () => {
    if (selectedBooking?.leadId) {
      navigate(`/pipelines/${selectedBooking.leadId}`);
    }
    handleMenuClose();
  };

  const handleEditBooking = () => {
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDownloadReceipt = () => {
    setSnackbar({
      open: true,
      message: `Downloading receipt for booking ${selectedBooking?._id}`,
      severity: "info",
    });
    handleMenuClose();
  };

  const handleCancelAppointment = () => {
    setCancelModalOpen(true);
    handleMenuClose();
  };

  const handleSaveBooking = async (updatedBooking) => {
    if (!selectedBooking) return;

    try {
      // Get the booking type from the selected booking or updated booking
      const bookingType = updatedBooking.type || selectedBooking.type || "appointment";

      await updateBookingApi(
        selectedBooking.leadId,
        bookingType,
        selectedBooking._id,
        updatedBooking
      );

      setSnackbar({
        open: true,
        message: "Booking updated successfully.",
        severity: "success",
      });
      setEditModalOpen(false);

      // Call the parent's onFilter to refresh data
      onFilter?.(activeFilters);
    } catch (error) {
      console.error("Error updating booking:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update booking",
        severity: "error",
      });
    }
  };

  const handleConfirmCancel = async (cancelData) => {
    if (!selectedBooking) return;

    try {
      // Get the booking type from the selected booking
      const bookingType = selectedBooking.type || "appointment";

      // Update booking status to cancelled with reason
      await updateBookingApi(
        selectedBooking.leadId,
        bookingType,
        selectedBooking._id,
        {
          status: "Cancelled",
          cancellation_reason: cancelData.reason,
          cancellation_notes: cancelData.notes,
        }
      );

      setSnackbar({
        open: true,
        message: "Booking cancelled successfully.",
        severity: "success",
      });
      setCancelModalOpen(false);

      // Call the parent's onFilter to refresh data
      onFilter?.(activeFilters);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to cancel booking",
        severity: "error",
      });
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleBookingTypeTabChange = (event, newValue) => {
    setBookingTypeTab(newValue);
    setPage(1);
  };

  // Filter bookings by type
  const filteredBookings = useMemo(() => {
    let filtered = flattenedBookings;

    // Filter by booking type tab
    if (bookingTypeTab !== "all") {
      filtered = filtered.filter((b) => {
        const type = b.type?.toLowerCase();
        if (bookingTypeTab === "test2") {
          return type === "test2" || type === "consultancy" || type === "appointment";
        }
        return type === bookingTypeTab;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((b) => {
        return (
          b.clientName?.toLowerCase().includes(query) ||
          b._id?.toLowerCase().includes(query) ||
          b.consultant?.name?.toLowerCase().includes(query)
        );
      });
    }

    // Apply active filters if set
    if (activeFilters) {
      // Status filter
      if (activeFilters.bookingStatuses) {
        const statusMap = {
          scheduled: "scheduled",
          confirmed: "confirmed",
          ongoing: "ongoing",
          completed: "completed",
          cancelled: "cancelled",
        };
        const activeStatuses = Object.entries(activeFilters.bookingStatuses)
          .filter(([_, isActive]) => isActive)
          .map(([key]) => statusMap[key]);

        if (activeStatuses.length > 0) {
          filtered = filtered.filter((b) => activeStatuses.includes(b.status?.toLowerCase()));
        }
      }

      // Consultant filter
      if (activeFilters.consultant) {
        filtered = filtered.filter((b) => {
          const consultantName = b.consultant?.name?.toLowerCase().replace(/\s+/g, "-");
          return consultantName === activeFilters.consultant;
        });
      }
    }

    return filtered;
  }, [flattenedBookings, bookingTypeTab, searchQuery, activeFilters]);

  const paginatedBookings = filteredBookings.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredBookings.length / rowsPerPage);

  // Format date for display
  const formatBookingDateTime = (booking) => {
    if (!booking.date) return "N/A";
    const dateStr = dayjs(booking.date).format("MMM DD, YYYY");
    const slotStr = booking.slot || "";
    return slotStr ? `${dateStr} • ${slotStr}` : dateStr;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Detailed Booking History
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search bookings..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
          <Badge
            color="primary"
            variant="dot"
            invisible={!activeFilters}
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: primaryColor,
                top: 4,
                right: 4,
              },
            }}
          >
            <IconButton
              onClick={handleFilterClick}
              sx={{
                color: filterAnchor || activeFilters ? primaryColor : colors.textMuted,
                border: `1px solid ${filterAnchor || activeFilters ? primaryColor : colors.borderLight}`,
                borderRadius: 2,
                p: 1,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                  borderColor: primaryColor,
                },
              }}
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Badge>
          {activeFilters && (
            <Chip
              label="Clear"
              size="small"
              onClick={handleClearFilters}
              sx={{
                height: 28,
                fontSize: 11,
                fontWeight: 500,
                bgcolor: colors.bgPrimary,
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                cursor: "pointer",
                "&:hover": {
                  bgcolor: colors.bgHover,
                  borderColor: colors.danger,
                  color: colors.danger,
                },
              }}
            />
          )}
        </Stack>
      </Stack>

      {/* Booking Type Tabs */}
      <Tabs
        value={bookingTypeTab}
        onChange={handleBookingTypeTabChange}
        sx={{
          mb: 2,
          "& .MuiTabs-indicator": {
            bgcolor: primaryColor,
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: colors.textSecondary,
            px: 2,
            minHeight: 40,
            "&.Mui-selected": {
              color: primaryColor,
            },
          },
        }}
      >
        <Tab label="All Bookings" value="all" />
        <Tab label="Consultancy" value="test2" />
        <Tab label="Treatment" value="treatment" />
      </Tabs>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              borderRadius: 4,
              boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
            },
          },
        }}
      >
        <BookingFilters
          onApply={handleApplyFilters}
          onClose={handleFilterClose}
          initialFilters={activeFilters || {}}
        />
      </Popover>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Client Name</TableCell>
            <TableCell sx={headerCellSx}>Booking Type</TableCell>
            <TableCell sx={headerCellSx}>Services</TableCell>
            <TableCell sx={headerCellSx}>Consultant</TableCell>
            <TableCell sx={headerCellSx}>Date & Time</TableCell>
            <TableCell sx={headerCellSx}>Status</TableCell>
            <TableCell sx={headerCellSx} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedBookings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ py: 6, textAlign: "center", border: "none" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <SearchIcon sx={{ fontSize: 48, color: colors.textMuted, opacity: 0.5 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: colors.textSecondary }}>
                    No bookings found
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                    {searchQuery ? `No bookings match "${searchQuery}"` : "No bookings available"}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : paginatedBookings.map((row) => {
            const statusStyle = getBookingStatusStyle(row.status, theme);
            const typeStyle = getBookingTypeStyle(row.type);

            return (
              <TableRow
                key={row._id}
                sx={{
                  borderBottom: `1px solid ${colors.borderLight}`,
                  "&:hover": {
                    bgcolor: colors.bgPrimary,
                    "& td": {
                      bgcolor: colors.bgPrimary,
                    },
                  },
                  "&:last-child td": {
                    borderBottom: "none",
                  },
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onClick={() => row.leadId && navigate(`/pipelines/${row.leadId}`)}
              >
                <TableCell sx={bodyCellSx}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: primaryColor,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {row.clientName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: colors.textPrimary,
                          lineHeight: 1.3,
                        }}
                      >
                        {row.clientName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: colors.textMuted,
                          mt: 0.25,
                        }}
                      >
                        #{row._id?.slice(-8).toUpperCase()}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Chip
                    label={typeStyle.label}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      bgcolor: typeStyle.bg,
                      color: typeStyle.color,
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  {row.leadData?.services && row.leadData.services.length > 0 ? (
                    <Stack direction="row" alignItems="center">
                      {row.leadData.services.slice(0, 3).map((service, idx) => {
                        const serviceStyle = getServiceStyle(service);
                        return (
                          <Box
                            key={idx}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              bgcolor: serviceStyle.bg,
                              color: serviceStyle.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              fontSize: 12,
                              fontWeight: 700,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              border: "2px solid #FFFFFF",
                              marginLeft: idx > 0 ? "-12px" : 0,
                              zIndex: row.leadData.services.length - idx,
                            }}
                            title={service}
                          >
                            {serviceStyle.letters}
                          </Box>
                        );
                      })}
                      {row.leadData.services.length > 3 && (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: colors.bgPrimary,
                            color: colors.textSecondary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontSize: 12,
                            fontWeight: 700,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            border: "2px solid #FFFFFF",
                            marginLeft: "-12px",
                            zIndex: 0,
                          }}
                          title={`+${row.leadData.services.length - 3} more services`}
                        >
                          +{row.leadData.services.length - 3}
                        </Box>
                      )}
                    </Stack>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: colors.textMuted,
                        fontStyle: "italic",
                      }}
                    >
                      No services
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    {row.consultant?.name || "Not assigned"}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: colors.textSecondary,
                    }}
                  >
                    {formatBookingDateTime(row)}
                  </Typography>
                </TableCell>

                <TableCell sx={bodyCellSx}>
                  <Chip
                    label={statusStyle.label}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                      bgcolor: statusStyle.bg,
                      color: statusStyle.color,
                      "& .MuiChip-label": {
                        px: 1.5,
                      },
                    }}
                  />
                </TableCell>

                <TableCell sx={bodyCellSx} align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, row);
                    }}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": {
                        bgcolor: colors.bgPrimary,
                        color: colors.textPrimary,
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Row Menu */}
      <BookingRowMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onViewDetails={handleViewDetails}
        onEditBooking={handleEditBooking}
        onDownloadReceipt={handleDownloadReceipt}
        onCancelAppointment={handleCancelAppointment}
        booking={selectedBooking}
      />

      {/* Edit Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveBooking}
        booking={selectedBooking}
      />

      {/* Cancel Modal */}
      <CancelAppointmentModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        booking={selectedBooking}
      />

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
        pt={2}
        borderTop={`1px solid ${colors.borderLight}`}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textMuted,
          }}
        >
          Showing {Math.min(filteredBookings.length, rowsPerPage)} of {filteredBookings.length} bookings
        </Typography>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: 13,
                fontWeight: 500,
                color: colors.textSecondary,
                "&.Mui-selected": {
                  bgcolor: primaryColor,
                  color: "#fff",
                  "&:hover": {
                    bgcolor: colors.primaryDark,
                  },
                },
              },
            }}
          />
        )}
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: 2,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            bgcolor: snackbar.severity === "error" ? colors.danger : primaryColor,
            color: "#fff",
            "& .MuiAlert-icon": {
              fontSize: 20,
              color: "#fff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingHistory;
